import React from 'react';
import { useParams } from 'react-router-dom';

import { GameContext } from '../contexts/game';
import { PlayerContext } from '../contexts/player';
import { getFromStore, putToStore } from '../utils/indexedDB';
import { addManualScoringAsync } from '../services/players';

export type TScoring = {
  playerCode: string;
  scorings: {
    taskId: string;
    formId: string;
    answer: string;
  }[];
};

export const useScoringOnBackground = () => {
  const { gameCode } = useParams();
  const [player] = React.useContext(PlayerContext);
  const [, , , isOnline] = React.useContext(GameContext);

  const playerCode = player.playerState?.code || '';

  React.useEffect(() => {
    if (!playerCode) return;

    (async () => {
      const scorings = await getFromStore<TScoring>(
        'scoring-queue',
        playerCode
      );

      if (!scorings)
        await putToStore('scoring-queue', { playerCode, scorings: [] });
    })();
  }, [playerCode]);

  React.useEffect(() => {
    if (!gameCode || !isOnline || !playerCode) return;

    (async () => {
      const scorings = await getFromStore<TScoring>(
        'scoring-queue',
        playerCode
      );
      if (!scorings || scorings.scorings.length === 0) return;

      // attach scoring data when sending
      const results = await Promise.allSettled(
        scorings.scorings.map(async (scoring) => {
          const resp = await addManualScoringAsync(
            gameCode,
            playerCode,
            scoring.taskId,
            scoring.formId,
            scoring.answer
          );
          return { scoring, status: resp.status };
        })
      );

      // collect only the successful ones
      const successful: { taskId: string; formId: string }[] = [];
      results.forEach((res) => {
        if (res.status === 'fulfilled' && res.value.status === 200) {
          successful.push({
            taskId: res.value.scoring.taskId,
            formId: res.value.scoring.formId
          });
        }
      });

      // remove successful scorings from queue
      if (successful.length > 0) {
        const filtered = scorings.scorings.filter(
          (s) =>
            !successful.some(
              (ok) => ok.taskId === s.taskId && ok.formId === s.formId
            )
        );

        await putToStore('scoring-queue', {
          ...scorings,
          scorings: filtered
        });
      }
    })();
  }, [gameCode, isOnline, playerCode]);
};
